import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../componentes/base';
import { Estado, Vazio } from '../componentes/estados';
import { Modal } from '../componentes/sobreposicao';
import { useToast } from '../componentes/toast';
import { get, mensagemDeErro } from '../nucleo/api';
import { useSessao, type ValorSessao } from '../nucleo/sessao';

/**
 * val4 (29/10, ficha `2026-10-29_malgo_FUN_simular-usuario-de-teste.md`): "Simular usuário de teste" no menu do perfil (casca
 * nova e antiga). Só para quem tem `admin.simular_usuario` (o papel TI; o Dono tem tudo) e fora de uma simulação (dentro dela
 * não se simula outro). A lista são as contas de teste deste workspace; escolher recarrega a página como a conta escolhida,
 * com a faixa "Simulando: <nome> · Voltar para mim" (`FaixaAmbiente`).
 */
export const PERMISSAO_SIMULAR = 'admin.simular_usuario';

export const podeSimular = (s: Pick<ValorSessao, 'pode' | 'simulacao'>) => s.pode(PERMISSAO_SIMULAR) && !s.simulacao;

interface OpcaoSimulacao {
  id: string;
  nome: string;
  usuario: string | null;
  papeis: string[];
  permitido: boolean;
}

export function ModalSimularUsuario({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const { t } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const opcoes = useQuery({ queryKey: ['/auth/simulacao/opcoes'], queryFn: () => get<{ itens: OpcaoSimulacao[] }>('/auth/simulacao/opcoes'), enabled: aberto, staleTime: 0 });
  const simular = async (id: string) => {
    setEscolhido(id);
    try {
      await s.simular(id);
    } catch (e) {
      setEscolhido(null);
      toast.erro(mensagemDeErro(e));
    }
  };
  return (
    <Modal aberto={aberto} aoFechar={aoFechar} titulo={t('simulacao.titulo')}>
      <div className="flex flex-col gap-3" data-simular-usuario="">
        <p className="text-sm text-tinta-3">{t('simulacao.texto')}</p>
        <Estado consulta={opcoes}>
          {(d) =>
            d.itens.length === 0 ? (
              <Vazio texto={t('simulacao.vazio')} />
            ) : (
              <ul className="flex flex-col divide-y divide-borda">
                {d.itens.map((o) => (
                  <li key={o.id} className="flex min-w-0 flex-wrap items-center gap-2 py-2" data-opcao-simulacao={o.id}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium" title={o.nome}>
                        {o.nome}
                      </span>
                      <span className="block truncate text-xs text-tinta-3" title={o.papeis.join(', ')}>
                        {o.permitido ? o.papeis.join(', ') : t('simulacao.perfilNaoPermitido')}
                      </span>
                    </span>
                    <Botao tamanho="sm" disabled={!o.permitido || (!!escolhido && escolhido !== o.id)} carregando={escolhido === o.id} onClick={() => void simular(o.id)} aria-label={`${t('simulacao.simular')}: ${o.nome}`}>
                      {t('simulacao.simular')}
                    </Botao>
                  </li>
                ))}
              </ul>
            )
          }
        </Estado>
      </div>
    </Modal>
  );
}
